const AUTH_IN_PROGRESS_KEY = 'auth-in-progress';
const INTENTIONAL_REDIRECT_KEY = 'intentional_auth_redirect';
const INTENDED_ROUTE_KEY = 'intendedRoute';
const AUTH_FLOW_MARKER_KEY = 'auth-flow-marker';
const SIGNED_OUT_AT_KEY = 'signed-out-at';

const AUTH_IN_PROGRESS_TTL_MS = 2 * 60 * 1000;
const AUTH_FLOW_TTL_MS = 5 * 60 * 1000;

const PAYMENT_FLAGS = {
  paymentCompleted: 'paymentCompleted',
  subscriptionType: 'subscriptionType',
  paymentSuccess: 'payment-success'
};

const PRE_PAYMENT_KEYS = {
  userId: 'pre-payment-user-id',
  userEmail: 'pre-payment-user-email',
  timestamp: 'pre-payment-timestamp',
  planId: 'pre-payment-plan',
  redirectInitiated: 'payment-redirect-initiated'
};

const safeJsonParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

const getOrCreateTabId = () => {
  try {
    let tabId = sessionStorage.getItem('tab-id');
    if (!tabId) {
      tabId = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('tab-id', tabId);
    }
    return tabId;
  } catch (error) {
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
};

const readAuthInProgress = () => {
  try {
    return safeJsonParse(localStorage.getItem(AUTH_IN_PROGRESS_KEY));
  } catch (error) {
    return null;
  }
};

const isAuthInProgressStale = (data) => {
  if (!data || !data.timestamp) return true;
  return Date.now() - data.timestamp > AUTH_IN_PROGRESS_TTL_MS;
};

export const setIntendedRoute = (route) => {
  if (!route) return;
  try {
    localStorage.setItem(INTENDED_ROUTE_KEY, route);
  } catch (error) {
    // Non-blocking.
  }
};

export const peekIntendedRoute = () => {
  try {
    return localStorage.getItem(INTENDED_ROUTE_KEY);
  } catch (error) {
    return null;
  }
};

export const consumeIntendedRoute = () => {
  const route = peekIntendedRoute();
  if (!route) return null;
  try {
    localStorage.removeItem(INTENDED_ROUTE_KEY);
  } catch (error) {
    // Non-blocking.
  }
  return route;
};

export const clearAuthRedirectFlags = () => {
  try {
    localStorage.removeItem(AUTH_IN_PROGRESS_KEY);
    sessionStorage.removeItem(INTENTIONAL_REDIRECT_KEY);
  } catch (error) {
    // Non-blocking.
  }
};

export const clearAuthInProgressIfOwned = () => {
  const currentTabId = getOrCreateTabId();
  const existing = readAuthInProgress();
  if (!existing || !existing.tabId || existing.tabId === currentTabId) {
    try {
      localStorage.removeItem(AUTH_IN_PROGRESS_KEY);
    } catch (error) {
      // Non-blocking.
    }
  }
};

export const prepareOAuthRedirect = async ({ intendedRoute, checkSession } = {}) => {
  const currentTabId = getOrCreateTabId();
  const existing = readAuthInProgress();

  if (existing && !isAuthInProgressStale(existing) && existing.tabId !== currentTabId) {
    if (typeof checkSession === 'function') {
      try {
        const hasSession = await checkSession();
        if (hasSession) {
          return { ok: false, reason: 'auth-in-progress' };
        }
      } catch (error) {
        try {
          localStorage.removeItem(AUTH_IN_PROGRESS_KEY);
        } catch (clearError) {
          // Non-blocking.
        }
      }
    } else {
      return { ok: false, reason: 'auth-in-progress' };
    }
  }

  if (existing && isAuthInProgressStale(existing)) {
    try {
      localStorage.removeItem(AUTH_IN_PROGRESS_KEY);
    } catch (error) {
      // Non-blocking.
    }
  }

  try {
    localStorage.setItem(AUTH_IN_PROGRESS_KEY, JSON.stringify({
      timestamp: Date.now(),
      tabId: currentTabId
    }));
    sessionStorage.setItem(INTENTIONAL_REDIRECT_KEY, 'true');
    setAuthFlowMarker();
  } catch (error) {
    // Non-blocking.
  }

  if (intendedRoute) {
    setIntendedRoute(intendedRoute);
  }

  return { ok: true };
};

export const setAuthFlowMarker = () => {
  const marker = {
    id: `auth-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    startedAt: Date.now()
  };

  try {
    sessionStorage.setItem(AUTH_FLOW_MARKER_KEY, JSON.stringify(marker));
  } catch (error) {
    // Non-blocking.
  }

  return marker;
};

export const readAuthFlowMarker = () => {
  try {
    return safeJsonParse(sessionStorage.getItem(AUTH_FLOW_MARKER_KEY));
  } catch (error) {
    return null;
  }
};

export const clearAuthFlowMarker = () => {
  try {
    sessionStorage.removeItem(AUTH_FLOW_MARKER_KEY);
  } catch (error) {
    // Non-blocking.
  }
};

export const hasFreshAuthFlowMarker = () => {
  const marker = readAuthFlowMarker();
  if (!marker || !marker.startedAt) return false;
  return Date.now() - marker.startedAt <= AUTH_FLOW_TTL_MS;
};

export const setSignedOutAt = () => {
  try {
    localStorage.setItem(SIGNED_OUT_AT_KEY, Date.now().toString());
  } catch (error) {
    // Non-blocking.
  }
};

export const clearSignedOutAt = () => {
  try {
    localStorage.removeItem(SIGNED_OUT_AT_KEY);
  } catch (error) {
    // Non-blocking.
  }
};

export const getSignedOutAt = () => {
  try {
    const raw = localStorage.getItem(SIGNED_OUT_AT_KEY);
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch (error) {
    return null;
  }
};

export const isSignedOutMarkerActive = () => Boolean(getSignedOutAt());

export const shouldBlockSessionHydration = () => isSignedOutMarkerActive();

export const clearPaymentFlags = () => {
  try {
    localStorage.removeItem(PAYMENT_FLAGS.paymentCompleted);
    localStorage.removeItem(PAYMENT_FLAGS.subscriptionType);
    sessionStorage.removeItem(PAYMENT_FLAGS.paymentSuccess);
  } catch (error) {
    // Non-blocking.
  }
};

export const isMatchingPrePaymentUser = (userId, markers) => {
  if (!userId || !markers?.userId) return false;
  return userId === markers.userId;
};

export const setPrePaymentMarkers = ({ session, planId } = {}) => {
  if (!session?.user?.id) return false;

  try {
    localStorage.setItem(PRE_PAYMENT_KEYS.userId, session.user.id);
    localStorage.setItem(PRE_PAYMENT_KEYS.userEmail, session.user.email || '');
    localStorage.setItem(PRE_PAYMENT_KEYS.timestamp, Date.now().toString());
    if (planId) {
      localStorage.setItem(PRE_PAYMENT_KEYS.planId, planId);
    }

    sessionStorage.setItem(PRE_PAYMENT_KEYS.redirectInitiated, 'true');
    return true;
  } catch (error) {
    return false;
  }
};

export const readPrePaymentMarkers = () => {
  try {
    const userId = localStorage.getItem(PRE_PAYMENT_KEYS.userId);
    const userEmail = localStorage.getItem(PRE_PAYMENT_KEYS.userEmail);
    const timestamp = localStorage.getItem(PRE_PAYMENT_KEYS.timestamp);
    const planId = localStorage.getItem(PRE_PAYMENT_KEYS.planId);
    const redirectInitiated = sessionStorage.getItem(PRE_PAYMENT_KEYS.redirectInitiated) === 'true';

    if (!userId) {
      return null;
    }

    return {
      userId,
      userEmail,
      timestamp: timestamp ? parseInt(timestamp, 10) : null,
      planId,
      redirectInitiated
    };
  } catch (error) {
    return null;
  }
};

export const clearPrePaymentMarkers = () => {
  try {
    Object.values(PRE_PAYMENT_KEYS).forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    // Non-blocking.
  }
};
